import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import Avatar from '../components/Avatar';

const ChevronLeft = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="15 18 9 12 15 6"/>
  </svg>
);
const CameraIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
    <circle cx="12" cy="13" r="4"/>
  </svg>
);

export default function EditProfile() {
  const navigate = useNavigate();
  const { currentUser, updateUser } = useApp();
  const fileRef = useRef();

  const [form, setForm] = useState({
    name: currentUser.name || '',
    university: currentUser.university || '',
    hometown: currentUser.hometown || '',
    bio: currentUser.bio || '',
    photo: currentUser.photo || '',
    isDriver: currentUser.isDriver || false,
    carMake: currentUser.car?.make || '',
    carModel: currentUser.car?.model || '',
    carColor: currentUser.car?.color || '',
  });
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handlePhoto = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { setError('Photo must be under 2MB.'); return; }
    const reader = new FileReader();
    reader.onload = ev => set('photo', ev.target.result);
    reader.readAsDataURL(file);
  };

  const handleSave = () => {
    if (!form.name.trim()) { setError('Name is required.'); return; }
    if (!form.university.trim()) { setError('University is required.'); return; }
    if (form.isDriver && (!form.carMake.trim() || !form.carModel.trim() || !form.carColor.trim())) {
      setError('Please fill in all car details.'); return;
    }
    setError('');
    updateUser(currentUser.id, {
      name: form.name, university: form.university,
      hometown: form.hometown, bio: form.bio, photo: form.photo,
      isDriver: form.isDriver,
      car: form.isDriver ? { make: form.carMake, model: form.carModel, color: form.carColor } : null,
      isRider: true,
    });
    setSaved(true);
    setTimeout(() => { setSaved(false); navigate(`/profile/${currentUser.id}`); }, 1200);
  };

  return (
    <div className="page">
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 28, paddingTop: 8 }}>
        <button onClick={() => navigate(-1)} style={{ background: 'none', padding: 4, color: 'var(--gray-300)', marginRight: 12 }}>
          <ChevronLeft />
        </button>
        <h1 style={{ fontSize: 22, fontWeight: 800 }}>Edit Profile</h1>
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {saved && <div className="alert alert-success">Profile saved!</div>}

      {/* Photo */}
      <div style={{ textAlign: 'center', marginBottom: 28 }}>
        <div style={{ position: 'relative', display: 'inline-block' }}>
          <Avatar user={{ ...currentUser, photo: form.photo, name: form.name }} size={88} />
          <button
            onClick={() => fileRef.current.click()}
            style={{
              position: 'absolute', bottom: 0, right: 0,
              width: 28, height: 28, borderRadius: '50%',
              background: 'var(--accent)', border: '2px solid var(--navy)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: 'white',
            }}
          >
            <CameraIcon />
          </button>
          <input ref={fileRef} type="file" accept="image/*" onChange={handlePhoto} style={{ display: 'none' }} />
        </div>
        <p style={{ fontSize: 12, color: 'var(--gray-500)', marginTop: 8 }}>Tap to change photo (max 2MB)</p>
      </div>

      <div className="form-group">
        <label className="form-label">Full Name</label>
        <input className="form-input" type="text" value={form.name}
          onChange={e => set('name', e.target.value)} />
      </div>
      <div className="form-group">
        <label className="form-label">University</label>
        <input className="form-input" type="text" value={form.university}
          onChange={e => set('university', e.target.value)} />
      </div>
      <div className="form-group">
        <label className="form-label">Hometown</label>
        <input className="form-input" type="text" placeholder="e.g. Chicago, IL"
          value={form.hometown} onChange={e => set('hometown', e.target.value)} />
      </div>
      <div className="form-group">
        <label className="form-label">Bio</label>
        <textarea className="form-input" rows={3} placeholder="Tell others about yourself..."
          value={form.bio} onChange={e => set('bio', e.target.value)}
          style={{ resize: 'none', lineHeight: 1.5 }} />
      </div>

      <div className="divider" />

      {/* Driver section */}
      <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 14 }}>Driver Settings</h3>
      <div style={{ display: 'flex', gap: 10, marginBottom: 20 }}>
        <button className={`btn ${form.isDriver ? 'btn-primary' : 'btn-ghost'}`} style={{ flex: 1 }}
          onClick={() => set('isDriver', true)}>I Drive</button>
        <button className={`btn ${!form.isDriver ? 'btn-primary' : 'btn-ghost'}`} style={{ flex: 1 }}
          onClick={() => set('isDriver', false)}>Rider Only</button>
      </div>

      {form.isDriver && (
        <>
          <div className="form-group">
            <label className="form-label">Car Make</label>
            <input className="form-input" type="text" placeholder="e.g. Honda"
              value={form.carMake} onChange={e => set('carMake', e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">Car Model</label>
            <input className="form-input" type="text" placeholder="e.g. Civic"
              value={form.carModel} onChange={e => set('carModel', e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">Car Color</label>
            <input className="form-input" type="text" placeholder="e.g. Blue"
              value={form.carColor} onChange={e => set('carColor', e.target.value)} />
          </div>
        </>
      )}

      <button className="btn btn-primary btn-full" onClick={handleSave}
        style={{ padding: '14px', fontSize: 15, marginTop: 8 }}>
        Save Changes
      </button>
    </div>
  );
}
